import { Segment, TextSegment, PersonSegment, DocumentSegment } from './types';

/**
 * Maps file extensions to their corresponding Font Awesome icon classes
 */
export const getFileIcon = (type: string): string => {
  const iconMap: Record<string, string> = {
    pdf: 'fa-file-pdf',
    doc: 'fa-file-word',
    docx: 'fa-file-word',
    txt: 'fa-file-alt',
    csv: 'fa-file-csv',
    xls: 'fa-file-excel',
    xlsx: 'fa-file-excel',
    ppt: 'fa-file-powerpoint',
    pptx: 'fa-file-powerpoint',
    jpg: 'fa-file-image',
    jpeg: 'fa-file-image',
    png: 'fa-file-image',
    gif: 'fa-file-image',
    zip: 'fa-file-archive',
    rar: 'fa-file-archive',
    default: 'fa-file',
  } as const;

  return iconMap[type.toLowerCase()] ?? iconMap.default;
};

/**
 * Calculates the input width based on content length
 */
export const calculateInputWidth = (content: string): string => {
  const MIN_WIDTH = 500;
  const MAX_WIDTH = 1000;
  const CHAR_WIDTH = 8;
  const PADDING = 16;

  if (!content) {
    return `${MIN_WIDTH}px`;
  }

  const calculatedWidth = Math.min(
    content.length * CHAR_WIDTH + PADDING,
    MAX_WIDTH
  );
  return `${Math.max(calculatedWidth, MIN_WIDTH)}px`;
};

/**
 * Type guard to check if a segment is a TextSegment
 */
const isTextSegment = (segment: Segment): segment is TextSegment => {
  return segment.type === 'text';
};

/**
 * Removes a segment (document or person) and handles merging adjacent text segments
 */
export const removeSegment = (
  segments: Segment[],
  index: number,
  setSegments: React.Dispatch<React.SetStateAction<Segment[]>>
): void => {
  if (index < 0 || index >= segments.length) {
    console.warn('Invalid index provided to removeSegment');
    return;
  }

  const newSegments = [...segments];

  // Check if segment is between two text segments
  const hasPreviousText = index > 0 && isTextSegment(segments[index - 1]);
  const hasNextText =
    index < segments.length - 1 && isTextSegment(segments[index + 1]);

  if (hasPreviousText && hasNextText) {
    // Merge adjacent text segments
    const prevSegment = segments[index - 1] as TextSegment;
    const nextSegment = segments[index + 1] as TextSegment;
    const mergedContent = prevSegment.content + nextSegment.content;

    newSegments.splice(index - 1, 3, {
      type: 'text',
      content: mergedContent,
    });
  } else {
    // Remove only the segment
    newSegments.splice(index, 1);
  }

  // Ensure there's always at least one text segment
  if (newSegments.length === 0) {
    newSegments.push({ type: 'text', content: '' });
  }

  setSegments(newSegments);
};

/**
 * Converts segments to HTML string for contenteditable
 * Mentions are rendered as non-editable inline elements, similar to draft-js
 */
export const segmentsToHtml = (segments: Segment[]): string => {
  return segments
    .map((segment) => {
      if (segment.type === 'text') {
        return segment.content
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      } else if (segment.type === 'document') {
        return `<span data-type="document" data-id="${
          segment.id
        }" data-name="${segment.name.replace(
          /"/g,
          '&quot;'
        )}" class="document-reference" contenteditable="false" spellcheck="false">[${segment.name
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')}]</span>`;
      } else if (segment.type === 'person') {
        return `<span data-type="person" data-id="${
          segment.id
        }" data-user-id="${segment.userId}" data-name="${segment.name.replace(
          /"/g,
          '&quot;'
        )}" class="person-reference" contenteditable="false" spellcheck="false">@${segment.name
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')}</span>`;
      }
      return '';
    })
    .join('');
};

/**
 * Parses HTML contenteditable content back to segments
 */
export const htmlToSegments = (html: string): Segment[] => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const segments: Segment[] = [];
  let currentText = '';

  const processNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      currentText += node.textContent || '';
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const dataType = element.getAttribute('data-type');

      // Save any accumulated text
      if (currentText.trim()) {
        segments.push({ type: 'text', content: currentText });
        currentText = '';
      }

      if (dataType === 'document') {
        const id = element.getAttribute('data-id');
        const name = element.getAttribute('data-name') || '';
        segments.push({
          type: 'document',
          id: id || '',
          name: name,
        });
      } else if (dataType === 'person') {
        const id = element.getAttribute('data-id') || '';
        const userId = element.getAttribute('data-user-id') || '';
        const name = element.getAttribute('data-name') || '';
        segments.push({
          type: 'person',
          id: id,
          name: name,
          userId: userId,
        });
      } else {
        // Process children of other elements
        const childNodes = Array.from(element.childNodes);
        childNodes.forEach((childNode) => processNode(childNode));
      }
    }
  };

  Array.from(tempDiv.childNodes).forEach(processNode);

  // Add any remaining text
  if (currentText.trim() || segments.length === 0) {
    segments.push({ type: 'text', content: currentText });
  }

  return segments.length > 0 ? segments : [{ type: 'text', content: '' }];
};

/**
 * Gets cursor position in contenteditable
 */
export const getCaretPosition = (element: HTMLElement): number => {
  let position = 0;
  const selection = window.getSelection();

  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    position = preCaretRange.toString().length;
  }

  return position;
};

/**
 * Sets cursor position in contenteditable
 */
export const setCaretPosition = (
  element: HTMLElement,
  position: number
): void => {
  const range = document.createRange();
  const selection = window.getSelection();

  let charCount = 0;
  const nodeStack: Node[] = [element];
  let node: Node | undefined;
  let foundStart = false;

  while (!foundStart && (node = nodeStack.pop())) {
    if (node.nodeType === Node.TEXT_NODE) {
      const nextCharCount = charCount + (node.textContent?.length || 0);
      if (position <= nextCharCount) {
        range.setStart(node, position - charCount);
        foundStart = true;
      } else {
        charCount = nextCharCount;
      }
    } else {
      let i = node.childNodes.length;
      while (i--) {
        const childNode = node.childNodes[i];
        if (childNode) {
          nodeStack.push(childNode);
        }
      }
    }
  }

  if (foundStart) {
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }
};

/**
 * Extracts all mentioned people from segments (similar to draft-js extractMentions)
 */
export const extractMentions = (segments: Segment[]): PersonSegment[] => {
  return segments.filter(
    (segment): segment is PersonSegment => segment.type === 'person'
  );
};

/**
 * Extracts all mentioned documents from segments
 */
export const extractDocuments = (segments: Segment[]): DocumentSegment[] => {
  return segments.filter(
    (segment): segment is DocumentSegment => segment.type === 'document'
  );
};

/**
 * Converts segments to raw data format (similar to draft-js convertToRaw)
 * Useful for serialization or API calls
 */
export const segmentsToRaw = (segments: Segment[]) => {
  return {
    blocks: segments.map((segment, index) => ({
      key: `block-${index}`,
      text: segment.type === 'text' ? segment.content : '',
      type: 'unstyled',
      depth: 0,
      inlineStyleRanges: [],
      entityRanges:
        segment.type === 'text'
          ? []
          : [
              {
                offset: 0,
                length: segment.type === 'document' ? `[${segment.name}]`.length : `@${segment.name}`.length,
                key: segment.id,
              },
            ],
      data: {},
    })),
    entityMap: segments.reduce((acc, segment, index) => {
      if (segment.type !== 'text') {
        acc[index] = {
          type: segment.type === 'document' ? 'document' : 'mention',
          mutability: 'IMMUTABLE',
          data: segment,
        };
      }
      return acc;
    }, {} as Record<number, any>),
  };
};
